import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../api';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

/**
 * ResetPasswordPage — Performance Club dark theme.
 *
 * Same transactional shape as ForgotPasswordPage: centered single
 * column, editorial-panel card, dark inputs, lime submit. Four render
 * states: verifying, invalid-token, form, success.
 */
export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        await authAPI.verifyResetToken(token);
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const brand = (
    <Link to="/" className="flex items-center gap-3 justify-center mb-6">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
      <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
    </Link>
  );

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-surface)' }}>
        <div className="w-full max-w-md">
          {brand}
          <div className="editorial-panel p-8 text-center">
            <div className="w-12 h-12 border-4 border-primary-200/30 border-t-primary-100 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-[var(--color-text-muted)]">Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // No token or invalid token
  if (!token || (!tokenValid && !verifying)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-surface)' }} data-testid="reset-password-invalid">
        <div className="w-full max-w-md">
          {brand}
          <div className="editorial-panel p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full border border-red-500/30 bg-red-500/10 flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10 text-red-200" />
            </div>
            <span className="hero-kicker">Invalid link</span>
            <h2 className="editorial-title text-3xl text-[var(--color-text)]">Reset link expired.</h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              {error || 'This password reset link is invalid or has expired.'}
            </p>
            <div className="space-y-3 pt-2">
              <Link
                to="/forgot-password"
                className="block w-full py-3 bg-gradient-to-r from-primary-500 to-primary-300 text-surface rounded-xl font-semibold text-center hover:from-primary-300 hover:to-primary-200 transition-all shadow-lg shadow-primary-500/20"
              >
                Request new reset link
              </Link>
              <Link
                to="/login"
                className="block w-full py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] font-medium text-[var(--color-text)] text-center hover:bg-white/[0.06] transition-colors"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-surface)' }} data-testid="reset-password-success">
        <div className="w-full max-w-md">
          {brand}
          <div className="editorial-panel p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full border border-primary-200/30 bg-primary-200/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-primary-100" />
            </div>
            <span className="hero-kicker">Done</span>
            <h2 className="editorial-title text-3xl text-[var(--color-text)]">Password reset.</h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <Link
              to="/login"
              className="block w-full py-3 bg-gradient-to-r from-primary-500 to-primary-300 text-surface rounded-xl font-semibold text-center hover:from-primary-300 hover:to-primary-200 transition-all shadow-lg shadow-primary-500/20 mt-2"
            >
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-surface)' }} data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {brand}
        <div className="editorial-panel p-8 space-y-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <ArrowLeft className="w-3 h-3" /> Back to login
          </Link>

          <div>
            <span className="hero-kicker">Reset password</span>
            <h1 className="mt-3 editorial-title text-3xl text-[var(--color-text)]">Set a new password.</h1>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              Create a strong password for your account.
            </p>
          </div>

          {error && (
            <div className="border border-red-500/30 bg-red-500/10 text-red-200 p-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">Must be at least 6 characters.</p>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary-500 to-primary-300 text-surface font-semibold py-3.5 rounded-xl hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-surface border-t-transparent rounded-full animate-spin" />
              ) : (
                'Reset password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
